import { UntypedFormGroup } from '@angular/forms';

/**
 * Session context for form telemetry (DEV-7892): what stays the same for the
 * whole form session, sent once on `form_loaded` and joined to every later
 * event by `form_session_id`. Pure functions, so they test without TestBed.
 */

type Scalar = string | number | boolean | null;

/** Longest string WP keeps in a telemetry map (RFTelemetry::MAP_STRING_MAX_CHARS). */
export const MAX_CHARS = 1024;

/** Telemetry key → mainForm control that holds the attribution value. */
const ATTRIBUTION_CONTROLS: Record<string, string> = {
	gbraid:        '_gbraid',
	wbraid:        '_wbraid',
	so:            '_so',
	oppref:        '_oppref',
	customer_id:   '_customerId',
	customer_name: '_customerName',
	first_page:    '_firstpage',
	last_page:     '_lastpage',
	site_referrer: '_referrer',
	af_token:      '_afToken',
};

function capped( value: unknown ): Scalar {

	if( typeof value === 'string' ) {
		return value === '' ? null : value.slice( 0, MAX_CHARS );
	}

	return typeof value === 'number' || typeof value === 'boolean' ? value : null;
}

/**
 * Attribution the form already sends to the ERP, plus the form's own URL and
 * the embedding page's URL (null across origins), both as sent, and whether
 * the site prefilled the addresses (`place_from` / `place_to`). Everything is
 * logged as is: logdb is our own store (DEV-7892 review).
 */
export function sessionContext( mainForm: UntypedFormGroup, win: Window ): Record<string, Scalar> {

	const out: Record<string, Scalar> = {};

	for( const [ key, control ] of Object.entries( ATTRIBUTION_CONTROLS ) ) {
		out[ key ] = capped( mainForm.get( control )?.value );
	}

	let parentPageUrl: Scalar = null;
	try {
		parentPageUrl = capped( win.parent.location.href );
	} catch {
		// Reading a cross-origin parent throws: a partner embed.
	}

	const query = new URLSearchParams( win.location.search );

	out['form_url']        = capped( win.location.href );
	out['parent_page_url'] = parentPageUrl;
	out['prefilled']       = !! ( query.get( 'place_from' ) || query.get( 'place_to' ) );

	return out;
}

/**
 * Signals that do not change within a session: automation flag, screen,
 * timezone, language, CPU cores and, where the browser has it, connection.
 */
export function browserSignals( win: Window ): Record<string, Scalar> {

	const nav = win.navigator as Navigator & { connection?: { effectiveType?: string, downlink?: number } };

	let timezone: Scalar = null;
	try {
		timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
	} catch {
		// Intl missing: leave null.
	}

	return {
		webdriver:       nav?.webdriver === true,
		screen:          win.screen ? win.screen.width + 'x' + win.screen.height : null,
		timezone,
		language:        capped( nav?.language ),
		cpu_cores:       typeof nav?.hardwareConcurrency === 'number' ? nav.hardwareConcurrency : null,
		connection_type: capped( nav?.connection?.effectiveType ),
		downlink:        typeof nav?.connection?.downlink === 'number' ? nav.connection.downlink : null,
	};
}

/** Telemetry key → mainForm control holding a form-variant flag (DEV-7892). */
export const FLAG_CONTROLS: Record<string, string> = {
	otp_enabled:                        '_otpEnabled',
	property_type_from_enabled:         '_propertyTypeFromEnabled',
	property_type_to_enabled:           '_propertyTypeToEnabled',
	property_type_details_from_enabled: '_propertyTypeDetailsFromEnabled',
	property_type_details_to_enabled:   '_propertyTypeDetailsToEnabled',
	upload_pictures_enabled:            '_uploadPicturesEnabled',
	moving_to_extended:                 'movingTo._isExtended',
	m2_enabled:                         '_m2Enabled',
	property_rooms_enabled:             '_propertyRoomsEnabled',
	consent_movers_enabled:             '_consentMoversEnabled',
	fcc_enabled:                        '_fccEnabled',
	email_relay_opt_in_visible:         'nameEmailPhone._emailRelayOptInVisible',
};

/** Longest step path plannedSteps() follows; more means a broken list. */
const MAX_PLANNED_STEPS = 20;

/** The form-variant flags as booleans; a flag whose control is missing is left out. */
export function formFlags( mainForm: UntypedFormGroup ): Record<string, boolean> {

	const out: Record<string, boolean> = {};

	for( const [ key, path ] of Object.entries( FLAG_CONTROLS ) ) {

		const control = mainForm.get( path );
		if( control ) {
			out[ key ] = !! control.value;
		}
	}

	return out;
}

/** The flags of `next` whose value differs from `prev`, with their new value. */
export function diffFlags( prev: Record<string, boolean>, next: Record<string, boolean> ): Record<string, boolean> {

	const out: Record<string, boolean> = {};

	for( const key of Object.keys( next ) ) {

		if( prev[ key ] !== next[ key ] ) {
			out[ key ] = next[ key ];
		}
	}

	return out;
}

/**
 * The steps the form will show, following `_next` from movingFrom until
 * `finished` (the linked list verifyDynamicSteps() rewires). Stops at a
 * missing step or a step already listed.
 */
export function plannedSteps( mainForm: UntypedFormGroup ): string[] {

	const out: string[] = [];
	let name = 'movingFrom';

	while( name !== 'finished' && out.length < MAX_PLANNED_STEPS && ! out.includes( name ) ) {

		const step = mainForm.get( name );
		if( ! step ) {
			break;
		}

		out.push( name );
		name = step.get( '_next' )?.value;
	}

	return out;
}

/** How an address was filled in (DEV-7892). */
export type AddressSource = 'prefill' | 'places' | 'typed' | 'prefill_edited';

/**
 * The address source after `event`: prefilled from the site's URL, picked
 * from a Google suggestion, or typed by the visitor. Typing into a prefilled
 * address makes it `prefill_edited`; typing over a Google pick makes it `typed`.
 */
export function nextAddressSource( current: AddressSource | null, event: 'prefill' | 'places' | 'user_input' ): AddressSource {

	if( event === 'prefill' || event === 'places' ) {
		return event;
	}

	return current === 'prefill' || current === 'prefill_edited' ? 'prefill_edited' : 'typed';
}
